import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'cibli-offline';
const DB_VERSION = 1;

// --- Stores ---

export interface VoiceAssistantAudio {
  key: string; // `${locale}/${messageKey}`
  blob: Blob;
  version: number;
  durationMs: number;
}

export interface QueuedFeedback {
  id: string; // uuid client → X-Idempotency-Key
  boothId: string | null;
  sessionId: string | null;
  category: string;
  comment: string;
  audioClips: { id: string; blob: Blob; durationMs: number }[];
  locale: string;
  source: string;
  flowStep: string | null;
  lastMessage: string | null;
  userEmail: string | null;
  userPhone: string | null;
  appVersion: string | null;
  healthSnapshot: Record<string, unknown> | null;
  status: 'QUEUED' | 'SENDING' | 'FAILED';
  retryCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface LocaleCache {
  key: string; // locale code
  data: Record<string, unknown>;
  fetchedAt: number;
}

// --- DB singleton ---

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('voiceAssistant')) {
          db.createObjectStore('voiceAssistant', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('feedbackQueue')) {
          db.createObjectStore('feedbackQueue', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('locales')) {
          db.createObjectStore('locales', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

// --- Voice Assistant ---

export async function getCachedAudio(key: string): Promise<VoiceAssistantAudio | undefined> {
  const db = await getDB();
  return db.get('voiceAssistant', key);
}

export async function setCachedAudio(audio: VoiceAssistantAudio): Promise<void> {
  const db = await getDB();
  await db.put('voiceAssistant', audio);
}

// --- Feedback Queue ---

export async function queueFeedback(item: QueuedFeedback): Promise<void> {
  const db = await getDB();
  await db.put('feedbackQueue', { ...item, updatedAt: Date.now() });
}

export async function getQueuedFeedback(id: string): Promise<QueuedFeedback | undefined> {
  const db = await getDB();
  return db.get('feedbackQueue', id);
}

export async function getAllQueuedFeedback(): Promise<QueuedFeedback[]> {
  const db = await getDB();
  return db.getAll('feedbackQueue');
}

export async function deleteQueuedFeedback(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('feedbackQueue', id);
}

export async function updateQueuedFeedback(item: QueuedFeedback): Promise<void> {
  const db = await getDB();
  await db.put('feedbackQueue', { ...item, updatedAt: Date.now() });
}

// --- Locale Cache ---

export async function getCachedLocale(key: string): Promise<LocaleCache | undefined> {
  const db = await getDB();
  return db.get('locales', key);
}

export async function setCachedLocale(item: LocaleCache): Promise<void> {
  const db = await getDB();
  await db.put('locales', item);
}

// --- Persistence hint ---

export async function requestPersistence(): Promise<boolean> {
  if ('storage' in navigator && 'persist' in navigator.storage) {
    return navigator.storage.persist();
  }
  return false;
}

// --- Init ---

export async function initOfflineDb(): Promise<void> {
  await getDB();
  await requestPersistence();
}
